/**
 * WorkbenchRedirect — small helpers that send legacy per-type URLs to
 * the unified workbench.
 *
 * Spec: .agent-os/specs/2026-05-26-instrument-trading-revamp/ (P2-15)
 * Mirrors the /market-data/curve-builder → /market-data/curves redirect
 * precedent (commit b5f4b3870 / earlier).
 *
 * Two flavors:
 *   <ToWorkbenchNew type="swap"/>       → /workbench?type=swap&action=new
 *   <ToWorkbenchList type="swap"/>      → /workbench?type=swap
 *   <ToWorkbenchByCode type="swap"/>    → /workbench?type=swap&code=:instrumentCode
 *                                          (reads :instrumentCode from the URL)
 *
 * Each redirect uses `replace` so the legacy URL isn't kept in browser
 * history — browser back from the workbench skips it.
 */

import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { InstrumentType } from './types';

export const ToWorkbenchNew: React.FC<{ type: InstrumentType }> = ({ type }) => (
  <Navigate to={`/instruments/workbench?type=${encodeURIComponent(type)}&action=new`} replace />
);

export const ToWorkbenchList: React.FC<{ type: InstrumentType }> = ({ type }) => (
  <Navigate to={`/instruments/workbench?type=${encodeURIComponent(type)}`} replace />
);

export const ToWorkbenchByCode: React.FC<{ type: InstrumentType }> = ({ type }) => {
  const { instrumentCode } = useParams<{ instrumentCode: string }>();
  const target = instrumentCode
    ? `/instruments/workbench?type=${encodeURIComponent(type)}&code=${encodeURIComponent(instrumentCode)}`
    : `/instruments/workbench?type=${encodeURIComponent(type)}`;
  return <Navigate to={target} replace />;
};
